/* import __COLOCATED_TEMPLATE__ from './optimize.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import { indexBy } from 'underscore';
import {
  jobs,
  jobGroups,
  jobGroupObjects,
  type JobId,
} from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from 'embercom/components/reporting/automation/ai-insights/optimize/conversation-explore-panel';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import { fetchTaskSuggestions } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/task-suggestions/task-suggestions-data-resources';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';

interface Args {}

export default class Optimize extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: any;
  @service declare navbarCollapsingService: $TSFixMe;
  @service declare reportingChartService: ReportingChartService;
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked selectedJobGroup: keyof typeof jobGroups = 'improveAnswers';
  @tracked selectedJobId: JobId | undefined = undefined;

  @tracked showSidePanel = false;
  @tracked selectedConversationId = 0;
  @tracked filters: LogicalFilter | undefined = undefined;
  @tracked attributeValuesToExplore: string[] = [];
  @tracked summaryType: SummaryTab | undefined = undefined;
  @tracked summaryText: string | undefined = undefined;
  @tracked summaryHeading: string | undefined = undefined;
  @tracked conversationIdsToLoad: number[] = [];
  @tracked jobIdFromQueryParams: JobId | undefined = undefined;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.jobIdFromQueryParams = this.router.currentRoute?.queryParams?.jobId;
  }

  @use taskSuggestionsResource = AsyncData<TaskSuggestion[]>(({ abortSignal }) => {
    return fetchTaskSuggestions(
      this.store,
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
      abortSignal,
    );
  });

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get notReadyToRenderJob() {
    return this.selectedJobId === undefined;
  }

  get serializedReport() {
    return {
      id: 'optimize',
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get taskSuggestions(): TaskSuggestion[] {
    let suggestions = this.taskSuggestionsResource.value || [];
    return [...suggestions];
  }

  get isTaskSuggestionsLoading() {
    return this.taskSuggestionsResource.isLoading;
  }

  @cached
  get jobs() {
    return indexBy(jobs(this.intl), (job) => job.id);
  }

  get isSubmenuCollapsed() {
    return this.navbarCollapsingService.collapsed;
  }

  get availableJobIds() {
    return jobGroups[this.selectedJobGroup];
  }

  get availableJobs() {
    return this.availableJobIds.map((id) => this.jobs[id]);
  }

  get selectedJob() {
    return this.jobs[this.selectedJobId as JobId];
  }

  get selectedJobMetricId() {
    return this.selectedJob.chartSeries[0].metric_id;
  }

  get selectedJobSubheading(): string {
    return this.selectedJob?.subheading || '';
  }

  get jobGroupObjects() {
    return jobGroupObjects;
  }

  get selectedJobGroupObject() {
    return (
      this.jobGroupObjects.find((jobGroupObject) => jobGroupObject.id === this.selectedJobGroup) ||
      this.jobGroupObjects[0]
    );
  }

  get shouldShowComingSoon() {
    return (
      (!this.appService.app.canSeeOptimizeCXScore && this.selectedJobGroup === 'checkQuality') ||
      this.selectedJobGroup === 'increaseCoverage'
    );
  }

  @action
  changeJobGroup(jobGroup: keyof typeof jobGroups) {
    this.selectedJobGroup = jobGroup;
    this.selectedJobId = undefined;
    this.trackAnalyticsEvent('job-group-changed', jobGroup);
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/optimize-fin-reasons',
      action,
      object,
      ...extraContext,
    });
  }

  @action
  changeJob(jobId: JobId) {
    this.selectedJobId = jobId;
    this.trackAnalyticsEvent('job-changed', jobId);

    // Clear auto-open query params when manually navigating to different jobs
    // This prevents unexpected drawer opening when switching between jobs
    this.router.replaceWith(this.router.currentRouteName, {
      queryParams: { suggestionId: null, showApiDocs: null, jobId: null },
    });
  }

  get attributeIds() {
    return [this.selectedJob.attributeId];
  }

  @action
  onShowConversationExplorer(
    selectedConversationId: number | null,
    filters: LogicalFilter,
    attributeValuesToExplore: string[],
    summaryType: SummaryTab,
    summaryText: string,
    summaryHeading?: string,
    conversationIdsToLoad?: number[],
  ) {
    if (this.canAccessDrillIn) {
      this.selectedConversationId = selectedConversationId || 0;
      this.filters = filters;
      this.attributeValuesToExplore = attributeValuesToExplore;
      this.summaryType = summaryType;
      this.summaryText = summaryText;
      this.summaryHeading = summaryHeading;
      this.showSidePanel = true;
      this.conversationIdsToLoad = conversationIdsToLoad || [];
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  get comingSoonDescription() {
    switch (this.selectedJobGroup) {
      case 'checkQuality':
        return this.intl.t(
          'components.reporting.automation.fin-insights.jobs.coming-soon-description-cx-score',
        );
      case 'increaseCoverage':
        return this.intl.t(
          'components.reporting.automation.fin-insights.jobs.coming-soon-description-involvement-rate',
        );
      default:
        return this.intl.t(
          'components.reporting.automation.fin-insights.jobs.coming-soon-description-cx-score',
        );
    }
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  @action
  closeConversationExplorer() {
    this.showSidePanel = false;
    this.selectedConversationId = 0;
    this.filters = undefined;
    this.attributeValuesToExplore = [];
    this.summaryType = undefined;
    this.summaryText = undefined;
    this.trackAnalyticsEvent('conversation-explorer-closed', 'conversation-explorer');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::Optimize': typeof Optimize;
  }
}
